import { graphql } from 'gatsby'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import useSiteSettings from '../../../../hooks/useSiteSettings'
import useCustomerDisplayName from '../../../../lib/auth/hooks/useCustomerDisplayName'
import { useUtilityMenuState } from '../../../../lib/header/state'
import { useSanityRoleAndContextBasedContent } from '../../../../lib/sanity'
import { isLoginMenuEntry } from '../../../../utils/menuEntryTools'
import { filterMenuEntriesToDisplay } from '../../../../utils/sanity/documents/menuEntry'
import { StyledFunctionComponent } from '../../../types'
import LoginLink from '../../LoginLink'
import LogoutLink from '../../LogoutLink'
import MenuEntryLink from '../MenuEntryLink'
import * as Styled from './styled'

const DesktopUtilityMenu: StyledFunctionComponent = ({ className }) => {
  const { isAnimated, isAuthenticated, isVisible } = useUtilityMenuState()
  const customerDisplayName = useCustomerDisplayName()
  const { utilityMenu: menuEntriesUnfiltered } = useSiteSettings() || {}

  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const menuEntries = useMemo(
    () => filterMenuEntriesToDisplay(shouldDisplayContent)(menuEntriesUnfiltered),
    [menuEntriesUnfiltered, shouldDisplayContent],
  )

  return (
    <nav className={className} aria-label="Utility Navigation" data-cy="utility-nav">
      {!isVisible ? null : (
        <Styled.NavMenuContainer $isAnimated={isAnimated}>
          <Styled.NavMenu>
            {!!customerDisplayName && (
              <Styled.NavMenuItem data-cy="header-user-name">
                Welcome, {customerDisplayName}
              </Styled.NavMenuItem>
            )}
            {menuEntries.map((entry) => (
              <React.Fragment key={entry.name}>
                {isLoginMenuEntry(entry) ? (
                  <>
                    {isAuthenticated ? (
                      <Styled.NavMenuItem>
                        <LogoutLink>Sign out</LogoutLink>
                      </Styled.NavMenuItem>
                    ) : (
                      <Styled.NavMenuItem data-cy="login-button">
                        <LoginLink>{entry.name}</LoginLink>
                      </Styled.NavMenuItem>
                    )}
                  </>
                ) : (
                  <Styled.NavMenuItem>
                    <MenuEntryLink
                      contextBasedContent={entry.contextBasedContent}
                      roleBasedContent={entry.roleBasedContent}
                      link={entry.link}
                    >
                      {entry.name}
                    </MenuEntryLink>
                  </Styled.NavMenuItem>
                )}
              </React.Fragment>
            ))}
          </Styled.NavMenu>
        </Styled.NavMenuContainer>
      )}
    </nav>
  )
}

const StyledDesktopUtilityMenu = styled(DesktopUtilityMenu)`
  box-sizing: content-box;
  // constrain to height of text
  height: 15px;
  padding: 16px 0;
  overflow: hidden;
  width: 100%;
`

export { StyledDesktopUtilityMenu as DesktopUtilityMenu }

export const utilityMenu = graphql`
  fragment utilityMenuFragment on SanitySiteSettings {
    utilityMenu {
      ...MenuEntryFragment
    }
  }
`
